import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ThumbsUp, ThumbsDown, Copy, Loader2, Trophy } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_HOST;

const examplePrompts = [
  "Explain quantum computing in simple terms",
  "Write a short poem about artificial intelligence",
  "What are the ethical implications of autonomous vehicles?",
  "Compare and contrast renewable energy sources",
  "How might climate change affect global food security?",
  "Describe the process of photosynthesis",
  "What are some effective strategies for reducing stress?",
  "Explain how blockchain technology works",
  "Discuss the future of space exploration",
  "What are the main challenges in achieving sustainable development?"
];

const getRandomPrompt = () => {
  return examplePrompts[Math.floor(Math.random() * examplePrompts.length)];
};

const ABTestInterface = () => {
  const [prompt, setPrompt] = useState(getRandomPrompt());
  const [modelAResponse, setModelAResponse] = useState('');
  const [modelBResponse, setModelBResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modelAFeedback, setModelAFeedback] = useState(null);
  const [modelBFeedback, setModelBFeedback] = useState(null);
  const [preferredModel, setPreferredModel] = useState(null);
  const [stats, setStats] = useState(null);
  const [voteSubmitted, setVoteSubmitted] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      const data = await response.json();
      setStats(data.results && data.results[0] ? data.results[0] : {
        total_votes: 0,
        model_a_votes: 0,
        model_b_votes: 0
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      // Set default stats if fetch fails
      setStats({
        total_votes: 0,
        model_a_votes: 0,
        model_b_votes: 0
      });
    }
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError('');
    setModelAResponse('');
    setModelBResponse('');
    setModelAFeedback(null);
    setModelBFeedback(null);
    setPreferredModel(null);
    setVoteSubmitted(false);

    try {
      // Call our API to get responses from two different AI models
      console.log(`${API_URL}`)
      const response = await fetch(`${API_URL}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to generate responses');
      }
      
      setModelAResponse(data.modelA);
      setModelBResponse(data.modelB);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to get responses from models: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const submitVote = async () => {
    if (!preferredModel) {
      setError('Please select your preferred model');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          modelA_response: modelAResponse,
          modelB_response: modelBResponse,
          preferred_model: preferredModel,
          modelA_feedback: modelAFeedback,
          modelB_feedback: modelBFeedback,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) throw new Error('Failed to submit vote');
      
      setVoteSubmitted(true);
      await fetchStats();
    } catch (err) {
      setError('Failed to submit vote');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span className="text-2xl font-bold">A/B Testing Platform</span>
            {stats && (
              <div className="text-sm text-muted-foreground">
                Total votes: {stats.total_votes} (A: {stats.model_a_votes}, B: {stats.model_b_votes})
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Textarea 
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter your prompt here..."
              className="min-h-[120px] text-base"
            />
            
            <Button 
              onClick={handleSubmit}
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Getting Responses...
                </>
              ) : (
                'Submit'
              )}
            </Button>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {(modelAResponse || modelBResponse) && (
        <>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className={`relative ${preferredModel === 'A' ? 'ring-2 ring-primary' : ''}`}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center text-xl">
                  Model A (Claude)
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(modelAResponse)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="min-h-[200px] mb-4 text-left whitespace-pre-wrap">
                  {modelAResponse}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setModelAFeedback('positive')}
                    className={modelAFeedback === 'positive' ? 'bg-green-100 hover:bg-green-200' : ''}
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setModelAFeedback('negative')}
                    className={modelAFeedback === 'negative' ? 'bg-red-100 hover:bg-red-200' : ''}
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className={`relative ${preferredModel === 'B' ? 'ring-2 ring-primary' : ''}`}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center text-xl">
                  Model B (Mistral)
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(modelBResponse)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="min-h-[200px] mb-4 text-left whitespace-pre-wrap">
                  {modelBResponse}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setModelBFeedback('positive')}
                    className={modelBFeedback === 'positive' ? 'bg-green-100 hover:bg-green-200' : ''}
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setModelBFeedback('negative')}
                    className={modelBFeedback === 'negative' ? 'bg-red-100 hover:bg-red-200' : ''}
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold mb-2">Select Preferred Model</h3>
                  <p className="text-sm text-muted-foreground">Which model provided the better response?</p>
                </div>
                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setPreferredModel('A')}
                    className={preferredModel === 'A' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}
                  >
                    <Trophy className="mr-2 h-4 w-4" />
                    Model A (Claude)
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setPreferredModel('B')}
                    className={preferredModel === 'B' ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}
                  >
                    <Trophy className="mr-2 h-4 w-4" />
                    Model B (Mistral)
                  </Button>
                </div>
                <Button
                  onClick={submitVote}
                  disabled={!preferredModel || voteSubmitted}
                  className="w-full max-w-md mt-4"
                >
                  {voteSubmitted ? 'Vote Submitted!' : 'Submit Vote'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ABTestInterface;
